import styles from "./MessageCard.module.css";
import avatarImage from "../../assets/icons/avatar.png";
import { useState, useEffect, useRef } from "react";
import { ENDPOINTS } from "../../utils/endpoints";
import api from "../../services/api";
import useAuthStore from "../../store/authStore";
import chatStore from "../../store/chatStore";
import { FiDownload } from "react-icons/fi";
import {
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaEdit,
  FaTrash,
  FaSave,
  FaTimes,
} from "react-icons/fa";
import { saveAs } from "file-saver";
import axios from "axios";

const EMOJI_OPTIONS = ["👍", "❤️", "😍", "🔥", "💯"];

export function formatDateTimeParts(input) {
  if (!input) return { date: "-", time: "-" };

  const dateObj = new Date(input);
  if (isNaN(dateObj.getTime())) return { date: "-", time: "-" };

  const date = dateObj.toLocaleDateString("en-GB");
  const time = dateObj.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return { date, time };
}

function renderMessage(message) {
  if (!message || message.trim() === "") return null;
  const words = message.split(/(\s+)/);
  return words.map((word, index) => {
    if (word.startsWith("@")) {
      return (
        <span key={index} className={styles.mention}>
          {word}
        </span>
      );
    }
    return word;
  });
}

const MessageCard = ({
  sender,
  messageData,
  channel,
  currentUserId,
  currentUserRole,
  onEdit,
  onDelete,
}) => {
  const { user, socket } = useAuthStore();
  const { newMessageReactionRecieved, setNewMessageReactionRecieved } =
    chatStore();
  const { date, time } = formatDateTimeParts(messageData.created_at);
  const {
    file_type: fileType,
    file_url: fileUrl,
    message,
    sender_id,
  } = messageData;

  const [showPicker, setShowPicker] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState(null);

  const editInputRef = useRef(null);

  const openImageModal = (url) => {
    setModalImageUrl(url);
    setShowImageModal(true);
  };

  const closeImageModal = (e) => {
    if (e.target.classList.contains(styles.imageModalOverlay)) {
      setShowImageModal(false);
      setModalImageUrl(null);
    }
  };

  const hasReactions =
    Array.isArray(messageData.reactions) &&
    messageData.reactions.some((r) => (r.count ?? 0) > 0);

  const [isEditing, setIsEditing] = useState(false);
  const [editedMessage, setEditedMessage] = useState(message || "");
  const isOwnMessage = sender_id === currentUserId;
  const isAdmin = currentUserRole === "administrator";

  const canEdit =
    (isOwnMessage || isAdmin) &&
    (fileType === "other" || fileType === null) &&
    message &&
    message.trim() !== "" &&
    !messageData.is_deleted;

  const canDelete = (isOwnMessage || isAdmin) && !messageData.is_deleted;

  const handleSaveEdit = () => {
    const trimmed = editedMessage.trim();
    if (trimmed && trimmed !== message) {
      onEdit(messageData.id, trimmed);
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditedMessage(message);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === "Escape") {
      handleCancelEdit();
    }
  };

  const downloadImage = () => saveAs(fileUrl, "image.jpg");
  const downloadOtherFileType = () => {
    const filename = fileUrl.split("/").pop() || "file";
    saveAs(fileUrl, filename);
  };

  const handleCardClick = () => {
    if (!isEditing) {
      setShowPicker(!showPicker);
    }
  };
  const handleEmojiClick = async (emoji) => {
    setShowPicker(false);
    try {
      const payload = { user_id: user.id, emoji };
      const res = await api.post(
        ENDPOINTS.REACT_TO_A_MESSAGE(messageData.id),
        payload
      );
      if (res.data.success) {
        socket.emit("send-message-reaction", {
          type: res.data.data.type,
          channelId: channel.id,
          userId: user.id,
          messageId: messageData.id,
          emoji: emoji,
          removedEmoji: res.data.data.removedEmoji,
        });
      }
    } catch (error) {
      console.error("Error toggling reaction:", error);
    }
  };
  useEffect(() => {
    if (isEditing && editInputRef.current) {
      const textarea = editInputRef.current;
      const length = editedMessage.length;

      // Set focus and move cursor to end
      textarea.focus();
      textarea.setSelectionRange(length, length);

      // Scroll to the end for longer text
      textarea.scrollTop = textarea.scrollHeight;
    }
  }, [isEditing, editedMessage]);
  const renderReactions = () => {
    const hasAnyReactions =
      Array.isArray(messageData.reactions) &&
      messageData.reactions.some((r) => (r.count ?? 0) > 0);

    if (!showPicker && !hasAnyReactions) return null;

    return (
      <div className={styles.reactions}>
        {EMOJI_OPTIONS.map((emoji, index) => {
          const count =
            messageData.reactions.find((r) => r.emoji === emoji)?.count ?? 0;

          return (
            <button
              key={index}
              className={`${styles.reactionButton} ${
                messageData.user_reaction === emoji
                  ? styles.activeEmojiButton
                  : ""
              }`}
              onClick={() => handleEmojiClick(emoji)}
            >
              <span className={styles.emoji}>{emoji}</span>
              <span className={styles.count}>{count}</span>
            </button>
          );
        })}
      </div>
    );
  };

  useEffect(() => {
    if (!messageData.reactions || messageData.reactions.length === 0) return;

    const total = messageData.reactions.reduce(
      (sum, r) => sum + (r.count ?? 0),
      0
    );

    if (total === 0) {
      setShowPicker(false);
    }
  }, [messageData.reactions.map((r) => r.count).join(",")]);

  const renderApplicationPreview = (url) => {
    const ext = url.split(".").pop().toLowerCase();
    let Icon = null;
    if (ext === "pdf") Icon = FaFilePdf;
    else if (ext === "doc" || ext === "docx") Icon = FaFileWord;
    else if (ext === "xls" || ext === "xlsx") Icon = FaFileExcel;

    return (
      <div className={styles.fileWrapper}>
        {Icon && <Icon size={40} className={styles.fileIcon} />}
        <span className={styles.fileName}>{url.split("/").pop()}</span>
        <div
          onClick={(e) => {
            e.stopPropagation();
            downloadOtherFileType();
          }}
          className={styles.downloadIcon}
        >
          <FiDownload size={18} />
        </div>
      </div>
    );
  };
  useEffect(() => {
    if (!newMessageReactionRecieved) return;
    if (messageData.id === newMessageReactionRecieved.messageId) {
      //1:add, 2:update, 3:delete
      if (newMessageReactionRecieved.type === 1) {
        const reaction = messageData.reactions.find(
          (m) => m.emoji === newMessageReactionRecieved.emoji
        );
        if (reaction) {
          reaction.count = (reaction.count ?? 0) + 1;
        } else {
          const newReaction = {
            emoji: newMessageReactionRecieved.emoji,
            count: 1,
          };
          messageData.reactions.push(newReaction);
        }
        if (newMessageReactionRecieved.userId === user.id) {
          messageData.user_reaction = newMessageReactionRecieved.emoji;
        }
      } else if (newMessageReactionRecieved.type === 2) {
        //Remove old one
        const reactionRemoved = messageData.reactions.find(
          (m) => m.emoji === newMessageReactionRecieved.removedEmoji
        );
        if (reactionRemoved) {
          reactionRemoved.count = (reactionRemoved.count ?? 0) - 1;
        } else {
          const newReaction = {
            emoji: newMessageReactionRecieved.removedEmoji,
            count: 0,
          };
          messageData.reactions.push(newReaction);
        }

        //Add new one
        const reaction = messageData.reactions.find(
          (m) => m.emoji === newMessageReactionRecieved.emoji
        );
        if (reaction) {
          reaction.count = (reaction.count ?? 0) + 1;
        } else {
          const newReaction = {
            emoji: newMessageReactionRecieved.emoji,
            count: 1,
          };
          messageData.reactions.push(newReaction);
        }

        if (newMessageReactionRecieved.userId === user.id) {
          messageData.user_reaction = newMessageReactionRecieved.emoji;
        }
      } else {
        const reaction = messageData.reactions.find(
          (m) => m.emoji === newMessageReactionRecieved.emoji
        );
        if (reaction) {
          reaction.count = (reaction.count ?? 0) - 1;
        } else {
          const newReaction = {
            emoji: newMessageReactionRecieved.emoji,
            count: 0,
          };
          messageData.reactions.push(newReaction);
        }

        if (newMessageReactionRecieved.userId === user.id) {
          messageData.user_reaction = null;
        }
      }
      setNewMessageReactionRecieved(null);
    }
  }, [
    newMessageReactionRecieved,
    setNewMessageReactionRecieved,
    messageData.id,
    user.id,
  ]);

  function extractImageUrl(message) {
    if (!message) return null;
    const urlRegex = /(https?:\/\/[^\s'"]+)/gi;
    const matches = message.match(urlRegex);
    if (!matches) return null;
    for (let rawUrl of matches) {
      let url = rawUrl.replace(/[)\]\}>,.;!?'"]+$/g, "");
      if (/\.(jpg|jpeg|png|gif|webp)(?:[?#][^\s]*)?$/i.test(url)) {
        return url;
      }
    }
    return null;
  }

  function extractFirstUrl(message) {
    if (!message) return null;
    const urlRegex = /(https?:\/\/[^\s]+)/gi;
    const matches = message.match(urlRegex);
    return matches ? matches[0] : null;
  }

  const [linkPreview, setLinkPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    const url = extractFirstUrl(message);
    if (!url) return;
    const fetchPreview = async () => {
      setLoadingPreview(true);
      try {
        const { data } = await axios.get(
          `https://api.allorigins.win/get?url=${encodeURIComponent(url)}`
        );
        const parser = new DOMParser();
        const doc = parser.parseFromString(data.contents, "text/html");
        const title =
          doc.querySelector("meta[property='og:title']")?.content ||
          doc.querySelector("title")?.innerText;
        const description =
          doc.querySelector("meta[property='og:description']")?.content ||
          doc.querySelector("meta[name='description']")?.content;
        const image =
          doc.querySelector("meta[property='og:image']")?.content || null;
        setLinkPreview({ url, title, description, image });
      } catch (err) {
        console.error("Preview fetch error", err);
      } finally {
        setLoadingPreview(false);
      }
    };
    fetchPreview();
  }, [message]);

  // if (messageData.is_deleted) {
  //   return (
  //     <div className={`${styles.card} ${styles.deletedCard}`}>
  //       <img src={avatarImage} className={styles.avatar} alt="user" />
  //       <div className={styles.chatCard}>
  //         <div className={styles.deletedMessage}>
  //           <em>🚫 This message was deleted</em>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  if (messageData.is_deleted) {
    return null;
  }
  return (
    <>
      <div
        className={`${styles.card} ${
          sender_id === user.id ? styles.activeCard : ""
        }`}
        onClick={handleCardClick}
      >
        <img src={avatarImage} className={styles.avatar} alt="user" />

        <div className={styles.chatCard}>
          <div className={styles.userInfo}>
            <div className={styles.username}>{sender}</div>
            <div className={styles.datetime}>
              {date} {time}
              {messageData.updated_at && !isEditing && (
                <span className={styles.editedLabel}> (edited)</span>
              )}
            </div>
            {(canEdit || canDelete) && !isEditing && (
              <div className={styles.messageActions}>
                {canEdit && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEditing(true);
                    }}
                    className={styles.actionBtn}
                    title="Edit message"
                  >
                    <FaEdit />
                  </button>
                )}
                {canDelete && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(messageData.id);
                    }}
                    className={styles.actionBtn}
                    title="Delete message"
                  >
                    <FaTrash />
                  </button>
                )}
              </div>
            )}
          </div>

          {isEditing ? (
            <div className={styles.editContainer}>
              <textarea
                value={editedMessage}
                ref={editInputRef}
                onChange={(e) => setEditedMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                className={styles.editInput}
                rows={3}
              />
              <div className={styles.editActions}>
                <button onClick={handleSaveEdit} className={styles.saveBtn}>
                  <FaSave /> Save
                </button>
                <button onClick={handleCancelEdit} className={styles.cancelBtn}>
                  <FaTimes /> Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              {message && message.trim() !== "" && (
                <div className={styles.message}>{renderMessage(message)}</div>
              )}

              {linkPreview && !extractImageUrl(message) && (
                <div className={styles.linkPreview}>
                  {linkPreview.image && (
                    <img
                      src={linkPreview.image}
                      alt="preview"
                      className={styles.linkImage}
                      onClick={(e) => {
                        e.stopPropagation();
                        openImageModal(linkPreview.image);
                      }}
                      style={{ cursor: "pointer" }}
                    />
                  )}
                  <div className={styles.linkContent}>
                    <div className={styles.linkTitle}>{linkPreview.title}</div>
                    <div className={styles.linkDesc}>
                      {linkPreview.description}
                    </div>
                    <a
                      href={linkPreview.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.linkUrl}
                    >
                      {linkPreview.url}
                    </a>
                  </div>
                </div>
              )}
              {fileType === "image" && fileUrl && (
                <div className={styles.imageWrapper}>
                  <img
                    src={fileUrl}
                    alt="chat media"
                    className={styles.mediaPreview}
                    onClick={(e) => {
                      e.stopPropagation();
                      openImageModal(fileUrl);
                    }}
                  />
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadImage();
                    }}
                    className={styles.downloadIcon}
                  >
                    <FiDownload size={18} />
                  </div>
                </div>
              )}

              {fileType === "video" && fileUrl && (
                <video controls className={styles.mediaPreview}>
                  <source src={fileUrl} type="video/mp4" />
                </video>
              )}

              {fileType === "application" &&
                fileUrl &&
                renderApplicationPreview(fileUrl)}

              {renderReactions()}
            </>
          )}
        </div>
      </div>

      {showImageModal && (
        <div className={styles.imageModalOverlay} onClick={closeImageModal}>
          <div className={styles.imageModalContent}>
            <img src={modalImageUrl} alt="Full preview" />
          </div>
        </div>
      )}
    </>
  );
};

export default MessageCard;
